/**
 * Phase 8 · C-13 — Library (networks / sources) page.
 *
 * Route: /library (protected by OrgGuard via AppRoot)
 *
 * Data: `librarySourceService.list(orgId)` via TanStack Query. Read-only in
 * this ticket — connect / disconnect / upload flows land in a follow-up.
 *
 * Layout:
 *   [PageHeader] title="Library"  CTA: [Share] + [+ New search]
 *   [tab row]  Sources
 *   ┌──────────────────────────────┬──────────────┐
 *   │  Sources grid (2col md 3xl)  │  Right rail  │
 *   │                              │  (360 px)    │
 *   └──────────────────────────────┴──────────────┘
 *
 * States:
 *   loading → skeleton card grid
 *   error   → inline banner "Could not load sources · Retry"
 *   empty   → minimalist CTA card "No sources yet — connect a network"
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Share2, Plus, AlertTriangle, RotateCw } from 'lucide-react';

import { PageHeader } from '@shared/components/ui/PageHeader';
import { SyncStatusBadge } from '@shared/components/SyncStatusBadge';
import { useGlobalToast } from '@shared/components/Toast/ToastProvider';
import { useAuth } from '@core/auth/useAuth';
import { queryKeys } from '@core/query/queryKeys';
import {
  librarySourceService,
  type LibrarySource,
  type LibrarySourceStatus,
} from '@core/supabase/services/librarySourceService';

import { SourceCard, AddNetworkCard } from './components/SourceCard';
import { NetworkSettingsRail } from './components/NetworkSettingsRail';
import { LibrarySourceDetailPanel } from './LibrarySourceDetailPanel';
import {
  type LibraryNetwork,
  type SourceFilter,
  type SyncStatus,
  syncStatusToFilter,
} from './types';

const FILTER_LS_KEY = 'ggj.library.filter';

function readStoredFilter(): SourceFilter {
  try {
    const raw = localStorage.getItem(FILTER_LS_KEY);
    if (raw === 'active' || raw === 'warn' || raw === 'disconnected') return raw;
  } catch { /* storage unavailable */ }
  return 'all';
}

// ── Adapter: DB row → UI shape ──────────────────────────────────────────────
//
// The UI card/drawer/rail components predate the DB schema and expect a
// richer `LibraryNetwork` object. Display-only fields that the DB doesn't
// carry yet (candidateCount, memberCount, privacy, uploader) fall back to
// safe defaults. Workers can denormalise the missing counts into dedicated
// columns later.

function dbStatusToSync(s: LibrarySourceStatus): SyncStatus {
  if (s === 'active')       return 'synced';
  if (s === 'warn')         return 'syncing';
  return 'failed';
}

function deriveInitials(name: string, iconKey: string | null): string {
  if (iconKey && iconKey.length <= 3) return iconKey.toUpperCase();
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]![0] ?? '';
  const second = parts[1]?.[0] ?? '';
  return (first + second).toUpperCase() || '?';
}

function sourceToNetwork(src: LibrarySource): LibraryNetwork {
  return {
    id: src.id,
    name: src.name,
    description: src.description ?? '',
    iconInitials: deriveInitials(src.name, src.iconKey),
    connectionCount: src.itemCount,
    candidateCount: 0,
    memberCount: 0,
    syncStatus: dbStatusToSync(src.status),
    uploadedBy: '',
    uploadedAt: src.createdAt,
    lastSynced: src.updatedAt,
    privacy: 'link',
    directSearchUrl: `/library/${src.id}/search`,
  };
}

// ── Filter chips ─────────────────────────────────────────────────────────────

const FILTER_CHIPS: { value: SourceFilter; label: string; dot: string }[] = [
  { value: 'all',          label: 'All',              dot: '#9CA3AF' },
  { value: 'active',       label: 'Active',           dot: '#10B981' },
  { value: 'warn',         label: 'Needs attention',  dot: '#F59E0B' },
  { value: 'disconnected', label: 'Disconnected',     dot: '#EF4444' },
];

function FilterChips({
  current,
  onChange,
}: {
  current: SourceFilter;
  onChange: (f: SourceFilter) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter sources">
      {FILTER_CHIPS.map(({ value, label, dot }) => {
        const active = current === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => onChange(value)}
            className={[
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[12px] font-medium transition-colors',
              active
                ? 'border-[#10B981] bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                : 'vscode-border vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary',
            ].join(' ')}
            aria-pressed={active}
          >
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: dot }}
              aria-hidden
            />
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ── Loading / error / empty blocks ─────────────────────────────────────────

function SkeletonCard() {
  return (
    <div
      className="rounded-[10px] border vscode-border p-4 animate-pulse"
      aria-hidden
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-[8px] vscode-bg-tertiary" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-2/3 rounded vscode-bg-tertiary" />
          <div className="h-2.5 w-1/3 rounded vscode-bg-tertiary" />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <div className="h-2.5 w-full rounded vscode-bg-tertiary" />
        <div className="h-2.5 w-4/5 rounded vscode-bg-tertiary" />
      </div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div
      className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
      aria-busy="true"
      aria-label="Loading sources"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

function ErrorCard({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-[10px] border border-amber-500/40 bg-amber-500/5 px-4 py-3 flex items-center gap-3">
      <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden />
      <p className="flex-1 text-[13px] vscode-text-primary">
        Could not load sources.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 rounded-[8px] border px-3 py-1 text-[12px] font-medium vscode-border vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
      >
        <RotateCw className="h-3.5 w-3.5" aria-hidden />
        Retry
      </button>
    </div>
  );
}

function EmptyStateCard({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="rounded-[10px] border border-dashed vscode-border p-8 flex flex-col items-center gap-3 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-[10px] vscode-bg-tertiary">
        <Plus className="h-5 w-5 vscode-text-tertiary" aria-hidden />
      </div>
      <p className="text-[13px] font-semibold vscode-text-primary">
        No sources yet — connect a network
      </p>
      <p className="max-w-sm text-[12px] vscode-text-secondary">
        Connect a LinkedIn export, Naukri account, or CSV upload to start
        building your talent library.
      </p>
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex items-center gap-1.5 rounded-[8px] bg-[#10B981] px-3 py-1.5 text-[13px] font-medium text-white transition-opacity hover:opacity-90"
      >
        <Plus className="h-4 w-4" aria-hidden />
        Connect a network
      </button>
    </div>
  );
}

// ── LibraryPage ──────────────────────────────────────────────────────────────

export function LibraryPage() {
  const { showToast } = useGlobalToast();
  const { user } = useAuth();
  const orgId = user?.orgId ?? undefined;

  // ── Real data ──
  const sourcesQuery = useQuery<LibrarySource[]>({
    queryKey: orgId ? queryKeys.library.sources(orgId) : ['library', 'none', 'sources'],
    queryFn: () => librarySourceService.list(orgId as string),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  });

  const networks = useMemo<LibraryNetwork[]>(
    () => (sourcesQuery.data ?? []).map(sourceToNetwork),
    [sourcesQuery.data],
  );

  // ── Filter chip state — persisted to localStorage ──
  const [filter, setFilter] = useState<SourceFilter>(readStoredFilter);

  const handleFilterChange = useCallback((f: SourceFilter) => {
    setFilter(f);
    try { localStorage.setItem(FILTER_LS_KEY, f); } catch { /* unavailable */ }
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(FILTER_LS_KEY);
      if (!stored) localStorage.setItem(FILTER_LS_KEY, 'all');
    } catch { /* unavailable */ }
  }, []);

  // ── Selection / drawer state ──
  const [selectedNetworkId, setSelectedNetworkId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  // Once data arrives, default-select the first network so the right rail
  // isn't empty.
  useEffect(() => {
    if (!selectedNetworkId && networks[0]) {
      setSelectedNetworkId(networks[0].id);
    }
  }, [networks, selectedNetworkId]);

  const detailNetwork = useMemo(
    () => networks.find((n) => n.id === detailId) ?? null,
    [networks, detailId],
  );

  const selectedNetwork = useMemo(
    () => networks.find((n) => n.id === selectedNetworkId) ?? null,
    [networks, selectedNetworkId],
  );

  const visibleNetworks = useMemo(
    () =>
      filter === 'all'
        ? networks
        : networks.filter((n) => syncStatusToFilter(n.syncStatus) === filter),
    [networks, filter],
  );

  // ── Write-action stubs (CTAs show a "coming soon" toast; no DB writes) ──
  const handleAddNetwork = useCallback(() => {
    showToast({
      variant: 'info',
      title: 'Connect a network',
      body: 'Network connect / CSV upload ships in the next slice.',
    });
  }, [showToast]);

  const handleUpdateNetworkStub = useCallback(() => {
    showToast({
      variant: 'info',
      title: 'Read-only for now',
      body: 'Source edits ship in the next slice.',
    });
  }, [showToast]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* ── Page header ────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 pb-4">
        <div className="min-w-0 flex-1">
          <SyncStatusBadge className="mb-2" />
          <PageHeader
            variant="minimal"
            breadcrumb={
              selectedNetwork
                ? [
                    { label: 'Library', href: '/library' },
                    { label: selectedNetwork.name },
                  ]
                : [{ label: 'Library', href: '/library' }]
            }
          />
        </div>

        <div className="flex flex-shrink-0 items-center gap-2 pt-1">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-[8px] border px-3 py-1.5 text-[13px] font-medium vscode-border vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
          >
            <Share2 className="h-4 w-4" aria-hidden />
            Share
          </button>
          <Link
            to="/smart-match/new"
            className="inline-flex items-center gap-1.5 rounded-[8px] bg-[#10B981] px-3 py-1.5 text-[13px] font-medium text-white transition-opacity hover:opacity-90"
          >
            <Plus className="h-4 w-4" aria-hidden />
            New search
          </Link>
        </div>
      </div>

      {/* ── Tab row ────────────────────────────────────────────── */}
      <div className="mb-4 flex border-b vscode-border">
        <button
          type="button"
          className="border-b-2 border-[#10B981] pb-2 pr-4 text-[13px] font-medium text-[#10B981]"
          aria-selected="true"
          role="tab"
        >
          Sources
        </button>
      </div>

      {/* ── Body ─────────────────────────────────────────────── */}
      <div className="flex min-h-0 flex-1 gap-6">
        <div className="min-w-0 flex-1 space-y-4 overflow-y-auto">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-2 shrink-0">
              <h2 className="text-[13px] font-semibold vscode-text-primary">
                Sources
                <span className="ml-1.5 text-[12px] font-normal vscode-text-tertiary">
                  ({sourcesQuery.isSuccess ? visibleNetworks.length : 0})
                </span>
              </h2>
            </div>
            <FilterChips current={filter} onChange={handleFilterChange} />
          </div>

          {sourcesQuery.isLoading ? (
            <SkeletonGrid />
          ) : sourcesQuery.isError ? (
            <ErrorCard onRetry={() => { void sourcesQuery.refetch(); }} />
          ) : networks.length === 0 ? (
            <EmptyStateCard onAdd={handleAddNetwork} />
          ) : visibleNetworks.length === 0 ? (
            <p className="py-6 text-center text-[13px] vscode-text-tertiary">
              No sources match this filter.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {visibleNetworks.map((net) => (
                <SourceCard
                  key={net.id}
                  network={net}
                  selected={selectedNetworkId === net.id}
                  onClick={() => {
                    setSelectedNetworkId(net.id);
                    setDetailId(net.id);
                  }}
                />
              ))}
              <AddNetworkCard onClick={handleAddNetwork} />
            </div>
          )}
        </div>

        {/* Right rail — 360 px. Mount only when a network is selected. */}
        {selectedNetwork ? (
          <div className="w-[360px] shrink-0">
            <NetworkSettingsRail
              network={selectedNetwork}
              onUpdate={handleUpdateNetworkStub}
            />
          </div>
        ) : null}
      </div>

      {/* ── Source detail drawer ─────────────────────────────── */}
      <LibrarySourceDetailPanel
        open={detailId !== null}
        network={detailNetwork}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
}
